import http from "./http.service";

/**
 * Planes y suscripcion de la cuenta. Mismo contrato que `subscription-state.service` de tienda-admin.
 *
 * El estado se cachea un rato: lo pide el menu lateral en cada pantalla del panel para mostrar los dias de
 * prueba que quedan, y no tiene sentido ir al back en cada navegacion. Se invalida al suscribirse.
 */
const TTL_MS = 60 * 1000;

class SuscripcionService {

    private cache: { estado: any; vence: number } | null = null;

    planes(): Promise<any> {
        return http.get("/public/planes");
    }

    estado(forzar = false): Promise<any> {
        const ahora = Date.now();
        if (!forzar && this.cache && this.cache.vence > ahora) {
            return Promise.resolve(this.cache.estado);
        }
        return http.get("/api/suscripcion/estado").then((r: any) => {
            this.cache = { estado: r.estado, vence: ahora + TTL_MS };
            return r.estado;
        });
    }

    /** Cancela la suscripcion paga: sigue vigente hasta el proximo cobro. Devuelve el estado nuevo. */
    cancelar(): Promise<any> {
        return http.post("/api/suscripcion/cancelar", {}).then((r: any) => {
            this.cache = null;
            return r.estado;
        });
    }

    pagos(): Promise<any> {
        return http.get("/api/suscripcion/pagos").then((r: any) => r.pagos || []);
    }

    iniciar(planId: number): Promise<any> {
        return http.post("/api/suscripcion/iniciar", { planId }).then((r: any) => {
            this.cache = null;
            return r.suscripcion;
        });
    }

    olvidar() {
        this.cache = null;
    }

    // --- Back office ---

    /** Uso del asistente con IA por cuenta en un mes ("2026-10"; sin mes, el actual). */
    adminUsoIa(mes?: string): Promise<any> {
        return http.get(`/api/admin/ia/uso${mes ? `?mes=${encodeURIComponent(mes)}` : ""}`);
    }

    adminListar(filtros: { situacion?: string; plan?: string; q?: string }): Promise<any> {
        const params = new URLSearchParams();
        Object.entries(filtros).forEach(([k, v]) => v && params.append(k, v));
        return http.get(`/api/admin/suscripciones?${params.toString()}`);
    }

    adminDetalleDeUsuario(userId: number): Promise<any> {
        return http.get(`/api/admin/suscripciones/usuario/${userId}`);
    }

    /** `dias` vacio = sin fin. */
    adminAsignarPlan(cuentaId: number, planId: number, dias?: number | null): Promise<any> {
        return http.post(`/api/admin/suscripciones/cuenta/${cuentaId}/asignar-plan`, { planId, dias: dias || null });
    }

    adminExtenderPrueba(cuentaId: number, dias: number): Promise<any> {
        return http.post(`/api/admin/suscripciones/cuenta/${cuentaId}/extender-prueba`, { dias });
    }

    adminDesarmadero(cuentaId: number, valor: boolean): Promise<any> {
        return http.post(`/api/admin/suscripciones/cuenta/${cuentaId}/desarmadero`, { valor });
    }

    adminCancelar(cuentaId: number): Promise<any> {
        return http.post(`/api/admin/suscripciones/cuenta/${cuentaId}/cancelar`, {});
    }
}

/** Como se nombra cada situacion de una cuenta en el back office (ver SuscripcionAdminDTOs.FilaDTO). */
export const SITUACIONES: Record<string, string> = {
    PRUEBA: "En prueba",
    PENDIENTE: "Pago pendiente",
    VENCIDA: "Prueba vencida",
    PAUSADA: "Pausada",
    CANCELADA: "Cancelada",
    ACTIVA: "Activa",
    FUNDADOR: "Fundador",
    SIN_SUSCRIPCION: "Sin suscripción",
};

/** Tono del chip: ok (verde), alerta (rojo), espera (naranja) o neutro. */
export const tonoSituacion = (s: string) =>
    ({ ACTIVA: "ok", FUNDADOR: "ok", PRUEBA: "espera", PENDIENTE: "espera", VENCIDA: "alerta", CANCELADA: "alerta", PAUSADA: "alerta" } as any)[s] || "neutro";

const suscripcionService = new SuscripcionService();
export default suscripcionService;

/** "$ 50.000" en pesos sin decimales, como los precios del sitio. */
export const formatoPrecio = (monto: any) =>
    "$ " + Number(monto || 0).toLocaleString("es-AR", { maximumFractionDigits: 0 });

/** dd/mm/aaaa */
export const formatoFecha = (fecha: any) =>
    fecha ? new Date(fecha).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

/** Lo que incluye un plan, en el orden en que se muestra en la card. */
export const featuresDe = (plan: any): string[] => [
    ...(plan.incluyeSubdominio ? [
        "Tu tienda propia en tunombre.deautopartes.ar",
        "Tu logo, tus colores y solo tus productos",
    ] : []),
    plan.maxPublicacionesActivas ? `Hasta ${plan.maxPublicacionesActivas} publicaciones activas` : "Publicaciones activas ilimitadas",
    plan.maxUsers ? `Hasta ${plan.maxUsers} usuarios de tu equipo` : "Usuarios ilimitados",
    "Cobrás con Mercado Pago, transferencia o efectivo",
    "Ofertas y descuentos sobre tu catálogo",
    "Estadísticas de ventas y de lo que buscan los compradores",
    "Chat con compradores y alertas de repuestos",
];
